import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Ticket, LogOut, CalendarPlus, Clock, CheckCircle, CalendarCheck } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCardsAccess, cardHeaders, cardTitle, cardBalance } from "@/components/booking/CardPayment";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(y, m - 1, day);
  const wd = dt.toLocaleDateString("de-DE", { weekday: "short" });
  return `${wd}, ${String(day).padStart(2, "0")}.${String(m).padStart(2, "0")}.${y}`;
};

const AptList = ({ title, items, Icon, tone, testId, empty }) => (
  <section className="bg-[#1A1A1A] rounded-lg border border-white/10 p-4 sm:p-5" data-testid={testId}>
    <h3 className="font-semibold flex items-center gap-2 mb-3"><Icon className={`w-4 h-4 ${tone}`} />{title} <span className="text-[#71717A] text-sm">({items.length})</span></h3>
    {items.length === 0 ? <p className="text-sm text-[#71717A]">{empty}</p> : (
      <ul className="divide-y divide-white/5">
        {items.map((a) => (
          <li key={a.appointment_id} className="py-2 flex justify-between gap-3 text-sm">
            <span>{fmtDate(a.date)}</span>
            <span className="text-[#A1A1AA] whitespace-nowrap">{a.time} – {a.end_time} Uhr</span>
          </li>
        ))}
      </ul>
    )}
  </section>
);

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/cards/login`, { method: "POST", headers: cardHeaders(), body: JSON.stringify({ email: email.trim(), code: code.trim() }) });
      const data = await res.json();
      if (res.ok) onLogin(data.session_token, email.trim());
      else setError(typeof data.detail === "string" ? data.detail : "Bitte E-Mail und Kartencode prüfen.");
    } catch {
      setError("Ein Fehler ist aufgetreten");
    }
    setLoading(false);
  };

  return (
    <form onSubmit={submit} className="bg-[#1A1A1A] rounded-lg p-5 sm:p-6 border border-white/10 space-y-4" data-testid="card-login-form">
      <div>
        <Label htmlFor="mk-email">E-Mail-Adresse</Label>
        <Input id="mk-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-[#0A0A0A] border-white/20" data-testid="input-mk-email" />
      </div>
      <div>
        <Label htmlFor="mk-code">Kartencode</Label>
        <Input id="mk-code" required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} autoComplete="off" placeholder="XXXX-XXXX-XXXX"
          className="mt-1 bg-[#0A0A0A] border-white/20 font-mono tracking-widest" data-testid="input-mk-code" />
      </div>
      {error && <p className="text-sm text-red-400" data-testid="mk-login-error">{error}</p>}
      <Button type="submit" disabled={loading || !email || !code} className="w-full bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c] font-semibold" data-testid="btn-mk-login">
        {loading ? "Anmeldung..." : "Anmelden"}
      </Button>
      <p className="text-xs text-[#71717A]">Aus Sicherheitsgründen wird der Zugang nach 5 Fehlversuchen für 15 Minuten gesperrt.</p>
    </form>
  );
};

export default function MeineKartePage() {
  const access = useCardsAccess();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);

  const load = async (token) => {
    const res = await fetch(`${API_URL}/api/cards/me`, { headers: cardHeaders({ "X-Card-Session": token }) });
    if (res.ok) setData(await res.json());
    else logout();
  };

  const login = (token, mail) => { setSession(token); setEmail(mail); load(token); };
  const logout = () => {
    if (session) fetch(`${API_URL}/api/cards/logout`, { method: "POST", headers: { "X-Card-Session": session } });
    setSession(null); setData(null);
  };
  const book = () => navigate(`/buchen${access.testMode ? "?kartentest=1" : ""}`, { state: { cardSession: session, cardEmail: email } });

  const card = data?.card;
  const apts = data?.appointments;
  const canBook = card && card.status === "active" && card.credits_remaining > 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <SEO title="Meine Trainingskarte | Personal Fußballtraining" description="Guthaben und Termine deiner Trainingskarte." path="/meine-karte" />
      <div className="border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 py-5 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#c1ff72] transition-colors"><ChevronLeft className="w-4 h-4" /> Startseite</Link>
          {session && <button onClick={logout} className="text-sm text-[#A1A1AA] hover:text-white inline-flex items-center gap-1" data-testid="btn-mk-logout"><LogOut className="w-4 h-4" /> Abmelden</button>}
        </div>
      </div>
      <main className="max-w-2xl mx-auto px-4 py-10 space-y-6">
        <div>
          <Ticket className="w-9 h-9 text-[#c1ff72] mb-3" />
          <h1 className="font-['Bebas_Neue'] text-4xl sm:text-5xl">MEINE <span className="text-[#c1ff72]">TRAININGSKARTE</span></h1>
          {access.testMode && <p className="text-xs text-blue-400 mt-1">TESTMODUS: nur für Admins sichtbar</p>}
        </div>

        {!access.enabled ? (
          <p className="text-[#A1A1AA]" data-testid="mk-not-available">Dieser Bereich ist bald verfügbar.</p>
        ) : !card ? (
          <LoginForm onLogin={login} />
        ) : (
          <>
            <section className="bg-[#c1ff72]/10 border border-[#c1ff72]/40 rounded-lg p-5" data-testid="mk-card">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-['Bebas_Neue'] text-2xl text-[#c1ff72]">{cardTitle(card)}</p>
                  <p className="text-sm">{card.training_name}</p>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-white/10" data-testid="mk-card-status">{card.status_label}</span>
              </div>
              <p className="mt-4 text-lg font-semibold" data-testid="mk-card-balance">{cardBalance(card)}</p>
              {card.credits_reserved > 0 && <p className="text-xs text-[#A1A1AA] mt-1">{card.credits_reserved} Einheit(en) für ausstehende Anfragen reserviert</p>}
              <p className="text-xs text-[#71717A] mt-3">Karten-ID {card.card_id}{card.valid_until && ` · gültig bis ${fmtDate(card.valid_until)}`}</p>
              <Button onClick={book} disabled={!canBook} className="mt-4 w-full sm:w-auto bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c] font-semibold" data-testid="btn-mk-book">
                <CalendarPlus className="w-4 h-4 mr-2" /> Neuen Termin buchen
              </Button>
            </section>
            <AptList title="Kommende Termine" items={apts.upcoming} Icon={CalendarCheck} tone="text-[#c1ff72]" testId="mk-upcoming" empty="Keine bestätigten Termine." />
            <AptList title="Ausstehende Anfragen" items={apts.pending} Icon={Clock} tone="text-[#f59e0b]" testId="mk-pending" empty="Keine offenen Anfragen." />
            <AptList title="Absolvierte Trainings" items={apts.completed} Icon={CheckCircle} tone="text-green-500" testId="mk-completed" empty="Noch keine absolvierten Trainings." />
          </>
        )}
      </main>
    </div>
  );
}
